import axios from "axios";
import { addMinutes } from "date-fns";
import { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useBookingsForLayer } from "@/api/hooks/useBookingsForLayer";
import { useLayerInfo } from "@/api/hooks/useLayerInfo";
import { useLayerView } from "@/api/hooks/useLayerView";
import { useMetadata } from "@/api/hooks/useMetadata";
import { useTree } from "@/api/hooks/useTree";
import { linkLayerInfoProperties } from "@/components/layout/Sidebar/Layers/LayerInfoModal";
import { checkHidden } from "@/components/layout/Sidebar/Layers/TreeItem";
import MapStage from "@/components/shared/map/stage/MapStage";
import { useGlobalStore } from "@/stores/globalStore";
import { useMapStore } from "@/stores/mapStore";
import { useProjectStore } from "@/stores/projectStore";
import { RolesEnum, useUserStore } from "@/stores/userStore";
import { formatLocalDateToAPI } from "@/utils/helpers/dates.helpers";
import { resolveLayerViewId } from "@/utils/layer/resolveLayerViewId";
import { processLayerData } from "@/utils/layer-utils";
import PointsLayer from "../point/PointsLayer";
import PolygonsLayer from "../polygon/PolygonsLayer";
import Tooltip from "../tooltip/Tooltip";
import MapCoverLayer from "./MapCoverLayer";

const dispatchLoaded = () => {
	const event = new CustomEvent("map-loaded");
	document.dispatchEvent(event);
};

const MapContainer = () => {
	const setPopupLayer = useGlobalStore((state) => state.setPopupLayer);
	const activeLayer = useGlobalStore((state) => state.activeLayer);
	const selection = useGlobalStore((state) => state.selection);
	const nodes = useProjectStore((state) => state.nodes);
	const setMapLayer = useMapStore((state) => state.setMapLayer);
	const setNoBookFilter = useMapStore((state) => state.setNoBookFilter);
	const setNoZoom = useMapStore((state) => state.setNoZoom);
	const role = useUserStore((state) => state.role);

	const node = nodes.find((n) => n.id === Number(activeLayer));
	const layer = useMemo(
		() => resolveLayerViewId(activeLayer, nodes) ?? Number(activeLayer),
		[activeLayer, nodes],
	);

	const { layers } = useTree();
	const { metadata } = useMetadata();

	const { data: layerInfo } = useLayerInfo(Number(layer));

	const { layerView, isSuccess: layerViewLoading } = useLayerView(
		Number(layer),
	);
	const { data: settings } = useQuery(["settings"], () =>
		axios.get("/settings.json"),
	);
	const { data, isSuccess } = useBookingsForLayer(
		Number(layer),
		formatLocalDateToAPI(selection.startDate),
		formatLocalDateToAPI(addMinutes(selection.endDate, -30)),
	);

	useEffect(() => {
		setMapLayer(Number(layer));
	}, [layer, setMapLayer]);

	useEffect(() => {
		if (isSuccess && layerViewLoading) {
			dispatchLoaded();
		}
	}, [isSuccess, layerViewLoading]);

	const currentNode = useMemo(() => {
		if (layerInfo && metadata?.layers) {
			return metadata.layers[layerInfo.info?.type_uid];
		}
		return null;
	}, [layerInfo, metadata]);

	const props = linkLayerInfoProperties(
		currentNode?.plugin_data,
		layerInfo?.info?.plugin_data,
	);

	const isPopup = useMemo(
		() =>
			!!props.find((prop) => prop.name === "popup" && prop.value === "true"),
		[props],
	);

	const layerProps = useMemo(() => {
		// @ts-expect-error map_node includes type_uid and plugin_data at runtime
		const typeUid = layerView?.view.map_node.type_uid;

		return processLayerData(
			typeUid,
			// @ts-expect-error map_node shape differs from LevelData in API typings
			layerView?.view.map_node,
			metadata?.layers,
		);
	}, [metadata, layerView]);

	useEffect(() => {
		let noZoom = false;
		let noBookFilter = false;

		layerProps?.forEach((prop) => {
			if (prop.name.toLowerCase().includes("#zoomdisable")) {
				noZoom = prop.value === true;
			}
			if (prop.name.toLowerCase().includes("#nobookfilter")) {
				noBookFilter = prop.value === true;
			}
		});

		setNoZoom(noZoom);
		setNoBookFilter(noBookFilter);
	}, [layerProps, setNoBookFilter, setNoZoom]);

	useEffect(() => {
		if (node && isPopup) {
			setPopupLayer(Number(layer));
		}
	}, [layer, isPopup, node, setPopupLayer]);

	const accessablePolygons = useMemo(
		() =>
			layerView?.polygons.filter((polygon) => {
				const curr = layers?.nodes.find((n) => n.id === polygon.id);
				if (!curr) return true;

				const isHidden = checkHidden(curr, metadata);

				return role === RolesEnum.Admin ? true : !isHidden;
			}),
		[layers?.nodes, layerView?.polygons, metadata, role],
	);

	return (
		<MapStage>
			<MapCoverLayer view={layerView?.view} />
			<PolygonsLayer polygons={accessablePolygons} />
			<PointsLayer
				colors={settings?.data?.colors || {}}
				nodes={metadata?.rawNodes}
				points={layerView?.points}
				options={layerView?.options}
				bookings={data?.bookings}
			/>
			<Tooltip />
		</MapStage>
	);
};

MapContainer.whyDidYouRender = true;

export default MapContainer;
